/* eslint-disable @next/next/no-script-component-in-head */
import { Partytown } from '@builder.io/partytown/react'
import cn from 'classnames'
import { PlausibleEvent, PopupType } from 'enums/plausible'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { whiteList as languagesWhitelist } from 'static/languages'
import useAmplitude from 'utils/hooks/useAmplitude'
import useDisablingZoom from 'utils/hooks/useDIsablingZoom'
import useFacebookSDK from 'utils/hooks/useFacebookSDK'
import usePlausible from 'utils/hooks/usePlausible'
import usePreviousRoute from 'utils/hooks/usePreviousRoute'
import useRedirectWithTokensFromQuery from 'utils/hooks/useRedirectWithTokensFromQuery'
import useTelegramMiniapp from 'utils/hooks/useTelegramMiniapp'
import useYandexMetrika from 'utils/hooks/useYandexMetrika'
import lazyWithPreload from 'utils/lazyWithPreload'
import InfoProvider from 'utils/providers/InfoProvider/InfoProvider'

import { appWithTranslation, useTranslation } from 'next-i18next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Script from 'next/script'

import LanguageProvider from 'layouts/LanguageProvider'

import '../styles/globals.scss'
import 'react-toastify/dist/ReactToastify.css'
import styles from './_app.module.scss'

const POPUP_TIMEOUT = 120 * 1000

const PopupLazy = lazyWithPreload<any>(() => import('components/Popup/Popup'))

const MyApp = ({ Component, pageProps }: AppProps) => {
  const router = useRouter()
  const redirectToApp = useRedirectWithTokensFromQuery()
  const [t] = useTranslation(['shared', 'seo', 'main'])
  useYandexMetrika()

  useDisablingZoom()
  const { initData } = useTelegramMiniapp()
  const { initFacebookSdk } = useFacebookSDK()

  useEffect(() => {
    // Redirect to tg auth page when in tg webapp
    if (
      !!initData &&
      router.pathname !== '/auth/telegram' &&
      router.pathname !== '/regulation/terms-and-conditions' &&
      router.pathname !== '/regulation/privacy-policy'
    ) {
      router.push('/auth/telegram')
    }
  }, [initData, router])

  const { initAmplitude } = useAmplitude()

  const referrer = usePreviousRoute()

  const setEvent = usePlausible()
  useEffect(() => {
    initAmplitude()
    initFacebookSdk()

    setEvent(PlausibleEvent.OpenedBinany, { referrer })

    PopupLazy.preload()
  }, [])

  const ssrHost = (pageProps as { ssrHost?: string })?.ssrHost
  const hostname = ssrHost || Cookies.get('hostname')
  const path = router.pathname
  useEffect(() => {
    const { click_id: clickId, offer, partner_id: partnerId, ref, sub_id8: subId8 } = router.query

    if (subId8) {
      Cookies.set('sub_id8', subId8, { expires: 45 })
    }

    if (clickId) {
      const id: string = Array.isArray(clickId) ? clickId.join('') : clickId
      Cookies.set('click_id', id, { expires: 45 })
    }
    if (partnerId) {
      const pid: string = Array.isArray(partnerId) ? partnerId.join('') : partnerId
      Cookies.set('partner_id', pid, { expires: 45 })
    }
    if (offer) {
      const pid: string = Array.isArray(offer) ? offer.join('') : offer
      Cookies.set('offer', pid, { expires: 45 })
    }
    if (ref) {
      const rf: string = Array.isArray(ref) ? ref.join('') : ref
      localStorage.setItem('_ref', rf)
    }
    if (clickId || partnerId || offer || ref) {
      router.replace(router.pathname)
    }
  }, [router])

  const [showScroll, setShowScroll] = useState(false)

  const cannonicalRef = useCallback(() => {
    const pathname = router.pathname.length > 1 ? router.pathname : ''
    return router.locale === router.defaultLocale
      ? `https://${hostname}${pathname}`
      : `https://${hostname}/${router.locale}${pathname}`
  }, [router])

  const [timeUPPopupOpen, setTimeUPPopupOpen] = useState(false)
  const [timeUPPopupAlreadyOpened, setTimeUPPopupAlreadyOpened] = useState(false)

  const tick = useRef(null)

  const scrollEventListener = useCallback(
    (e) => {
      const { scrollTop } = e.target.documentElement
      setShowScroll(router.pathname.startsWith('/auth') ? false : scrollTop > 0)
    },
    [setShowScroll, router.pathname],
  )

  const scrollToTop = useCallback(() => {
    setEvent(PlausibleEvent.ClickedScrollUp, { referrer })
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', scrollEventListener)
    return () => window.removeEventListener('scroll', scrollEventListener)
  }, [scrollEventListener])

  useEffect(() => {
    if (
      !router.pathname.startsWith('/auth') &&
      !timeUPPopupOpen &&
      router.pathname !== '404' &&
      router.pathname !== '500'
    ) {
      tick.current = setTimeout(() => {
        setTimeUPPopupOpen(true)
        setTimeUPPopupAlreadyOpened(true)

        setEvent(PlausibleEvent.PopupDisplayed, {
          popupType: PopupType.OnTimeSpacing,
          referrer,
          locale: router.locale,
          domain: window.location.host,
          IP: router.locale,
        })
      }, POPUP_TIMEOUT)
    } else {
      clearInterval(tick.current)
    }

    return () => clearInterval(tick.current)
  }, [timeUPPopupOpen, router.pathname, setTimeUPPopupOpen, setTimeUPPopupAlreadyOpened])

  useEffect(() => {
    localStorage.setItem('landingLanguagesList', JSON.stringify(languagesWhitelist))
  }, [])

  useEffect(() => {
    function redirectToDashBoard() {
      const tokenAvailable = Boolean(localStorage.getItem('accessToken'))
      if (tokenAvailable) {
        window.location.assign(process.env.NEXT_PUBLIC_REACT_DASHBOARD_LINK)
      }
    }
    window.addEventListener('storage', redirectToDashBoard)

    return () => window.removeEventListener('storage', redirectToDashBoard)
  }, [])

  return (
    <>
      {!redirectToApp && (
        <>
          <Head>
            {hostname && hostname !== process.env.NEXT_PUBLIC_BN_HOSTNAME && (
              <>
                <link rel="alternate" hrefLang="en" href={`https://binany.com${path}`} />
                <link rel="alternate" hrefLang="hi" href={`https://binany.com/hi${path}`} />
                <link rel="alternate" hrefLang="bn" href={`https://binany.com/bn${path}`} />
                <link rel="alternate" hrefLang="id" href={`https://binany.com/id${path}`} />
                <link rel="alternate" hrefLang="ms" href={`https://binany.com/ms${path}`} />
                <link rel="alternate" hrefLang="my" href={`https://binany.com/my${path}`} />
                <link rel="alternate" hrefLang="am" href={`https://binany.com/am${path}`} />
                <link rel="alternate" hrefLang="pa" href={`https://binany.com/pa${path}`} />
                <link rel="alternate" hrefLang="si" href={`https://binany.com/si${path}`} />
                <link rel="alternate" hrefLang="sw" href={`https://binany.com/sw${path}`} />
                <link rel="alternate" hrefLang="vi" href={`https://binany.com/vi${path}`} />
                <link rel="alternate" hrefLang="ha" href={`https://binany.com/ha${path}`} />
                <link rel="alternate" hrefLang="ar" href={`https://binany.com/ar${path}`} />
                <link rel="alternate" hrefLang="pt" href={`https://binany.com/pt${path}`} />
                <link rel="alternate" hrefLang="pt-BR" href={`https://binany.com/pt-BR${path}`} />
                <link rel="alternate" hrefLang="th" href={`https://binany.com/th${path}`} />
                <link rel="alternate" hrefLang="es" href={`https://binany.com/es${path}`} />
                <link rel="alternate" hrefLang="x-default" href={`https://binany.com${path}`} />
              </>
            )}
            {hostname && hostname === process.env.NEXT_PUBLIC_BN_HOSTNAME && (
              <>
                <link
                  rel="alternate"
                  hrefLang="en"
                  href={`https://${process.env.NEXT_PUBLIC_BN_HOSTNAME}/en-BD${path}`}
                />
                <link
                  rel="alternate"
                  hrefLang="bn"
                  href={`https://${process.env.NEXT_PUBLIC_BN_HOSTNAME}${path}`}
                />
                <link
                  rel="alternate"
                  hrefLang="x-default"
                  href={`https://${process.env.NEXT_PUBLIC_BN_HOSTNAME}${path}`}
                />
              </>
            )}
            {hostname && <link rel="canonical" href={cannonicalRef()} />}

            <link
              rel="apple-touch-icon"
              sizes="57x57"
              href="/images/favicon/apple-icon-57x57.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="60x60"
              href="/images/favicon/apple-icon-60x60.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="72x72"
              href="/images/favicon/apple-icon-72x72.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="76x76"
              href="/images/favicon/apple-icon-76x76.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="114x114"
              href="/images/favicon/apple-icon-114x114.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="120x120"
              href="/images/favicon/apple-icon-120x120.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="144x144"
              href="/images/favicon/apple-icon-144x144.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="152x152"
              href="/images/favicon/apple-icon-152x152.png"
            />
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="/images/favicon/apple-icon-180x180.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="192x192"
              href="/images/favicon/android-icon-192x192.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="/images/favicon/favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="96x96"
              href="/images/favicon/favicon-96x96.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/images/favicon/favicon-16x16.png"
            />
            <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
          </Head>

          <Partytown forward={['dataLayer.push']} />

          <Script
            type="text/partytown"
            strategy="afterInteractive"
            id="gtm-script"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer',"${process.env.NEXT_PUBLIC_REACT_GTM_ID}");`,
            }}
          />
          <noscript>
            <iframe
              title="Google Tag Manager"
              src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_REACT_GTM_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
          <Script
            src="//web.webpushs.com/js/push/d8c455ba8312ea603b3295eeb9c25769_1.js"
            strategy="lazyOnload"
            async
          />
          <Script src="https://connect.facebook.net/en_US/sdk.js" strategy="lazyOnload" async />

          <Script
            strategy="afterInteractive"
            id="tg-mini-app"
            src="https://telegram.org/js/telegram-web-app.js"
          />
          <Script
            strategy="afterInteractive"
            id="af-sdk"
            src={process.env.NEXT_PUBLIC_AF_SDK_URL}
          />
          <LanguageProvider>
            <InfoProvider>
              <Component host={hostname} referrer={referrer} {...pageProps} />

              {timeUPPopupOpen && (
                <PopupLazy
                  t={t}
                  setOpen={setTimeUPPopupOpen}
                  popupType={PopupType.OnTimeSpacing}
                  referrer={referrer}
                />
              )}
            </InfoProvider>
          </LanguageProvider>

          <button
            onClick={scrollToTop}
            className={cn(styles.scrollButton, { [styles.shown]: showScroll })}
            id="common-app-scroll_up-button"
          >
            <div className={styles.imageWrapper}>
              <Image fill priority src="/images/util/scrollup.svg" alt="scrollup" />
            </div>
          </button>

          <ToastContainer />
        </>
      )}
    </>
  )
}

export default appWithTranslation(MyApp)
